import React from 'react';
import SEO from '../SEO/SEO';
import { getSEOData } from '../../data/seoData';
import { TOOL_TYPES } from '../../store/useToolStore';
import HorizontalToolScroller from './HorizontalToolScroller';
import { TOOL_SLUGS } from '../../data/toolSlugs';

const getToolPath = (toolType) => {
  return TOOL_SLUGS.find((t) => t.toolType === toolType)?.seoPath || '/';
};

const TOOL_ROUTES = {
  [TOOL_TYPES.LOADER]: { path: getToolPath(TOOL_TYPES.LOADER), icon: '⚙️', name: 'Loader Generator', description: 'Create animated loading indicators' },
  [TOOL_TYPES.GRADIENT]: { path: getToolPath(TOOL_TYPES.GRADIENT), icon: '🌈', name: 'Gradient Generator', description: 'Design beautiful gradients' },
  [TOOL_TYPES.CARD]: { path: getToolPath(TOOL_TYPES.CARD), icon: '🪪', name: 'Card Generator', description: 'Build reusable card components' },
  [TOOL_TYPES.ANIMATION]: { path: getToolPath(TOOL_TYPES.ANIMATION), icon: '⚡', name: 'Animation Generator', description: 'Create smooth animations' },
  [TOOL_TYPES.ALERT]: { path: getToolPath(TOOL_TYPES.ALERT), icon: '🔔', name: 'Alert Generator', description: 'Custom alert dialogs' },
  [TOOL_TYPES.PHOTO_PICKER]: { path: getToolPath(TOOL_TYPES.PHOTO_PICKER), icon: '📸', name: 'Photo Picker', description: 'Camera & library integration' },
  [TOOL_TYPES.SHADOW]: { path: getToolPath(TOOL_TYPES.SHADOW), icon: '🕶️', name: 'Shadow Generator', description: 'Generate .shadow() modifiers' },
  [TOOL_TYPES.BUTTON]: { path: getToolPath(TOOL_TYPES.BUTTON), icon: '🔘', name: 'Button Builder', description: 'Build styled SwiftUI buttons' },
  [TOOL_TYPES.GRID]: { path: getToolPath(TOOL_TYPES.GRID), icon: '🧩', name: 'Grid Generator', description: 'Generate LazyVGrid layouts' },
  [TOOL_TYPES.PALETTE]: { path: getToolPath(TOOL_TYPES.PALETTE), icon: '🎨', name: 'Color Palette', description: 'Generate named SwiftUI colors' },
  [TOOL_TYPES.JSON_TO_MODEL]: { path: getToolPath(TOOL_TYPES.JSON_TO_MODEL), icon: '🔍', name: 'JSON to Codable', description: 'Convert JSON to Swift models' },
  [TOOL_TYPES.API_GENERATOR]: { path: getToolPath(TOOL_TYPES.API_GENERATOR), icon: '🌐', name: 'API Generator', description: 'Generate API client code' },
  [TOOL_TYPES.STORYBOARD_CONVERTER]: { path: getToolPath(TOOL_TYPES.STORYBOARD_CONVERTER), icon: '🧰', name: 'Storyboard Converter', description: 'UIKit to SwiftUI migration' },
  [TOOL_TYPES.POSTMAN_IMPORTER]: { path: getToolPath(TOOL_TYPES.POSTMAN_IMPORTER), icon: '📝', name: 'Postman Importer', description: 'Import Postman collections' },
};

const Home = () => {
  const seoData = getSEOData('home');
  const allTools = TOOL_SLUGS
    .filter((t) => t.toolType !== TOOL_TYPES.STORYBOARD_CONVERTER)
    .map((t) => [t.toolType, TOOL_ROUTES[t.toolType]])
    .filter(([_, route]) => Boolean(route));

  return (
    <>
      <SEO
        title={seoData.title}
        description={seoData.description}
        keywords={seoData.keywords}
        canonical={seoData.canonical}
        image={seoData.image}
        jsonLd={seoData.jsonLd}
        type="website"
      />
      <div className="bg-white dark:bg-gray-900 transition-colors duration-200">
        {/* Hero Section */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="text-center">
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 dark:text-white mb-6">
              SwiftToolsKit
            </h1>
            <p className="text-xl md:text-2xl text-gray-700 dark:text-gray-300 mb-12 max-w-3xl mx-auto">
              Generate Swift & SwiftUI code instantly. Free tools for iOS developers to create loaders, gradients, cards, animations, and more.
            </p>
          </div>
        </div>

        {/* Tools Showcase - Category Tabs + Horizontal Scroller */}
        <div id="tools" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white text-center mb-8">All Tools</h2>

          {/* Tools Scroller */}
          <HorizontalToolScroller tools={allTools} />
        </div>

        {/* Features Section */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white text-center mb-12">Why SwiftToolsKit?</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-6 transition-colors duration-200">
              <div className="text-4xl mb-4">⚡</div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Fast & Free</h3>
              <p className="text-gray-600 dark:text-gray-400">Generate production-ready Swift code in seconds. No signup required.</p>
            </div>
            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-6 transition-colors duration-200">
              <div className="text-4xl mb-4">🎨</div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Visual Preview</h3>
              <p className="text-gray-600 dark:text-gray-400">See your code in action with real-time preview before copying.</p>
            </div>
            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-6 transition-colors duration-200">
              <div className="text-4xl mb-4">📦</div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Copy & Paste</h3>
              <p className="text-gray-600 dark:text-gray-400">Get ready-to-use code that you can paste directly into Xcode.</p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Home;

